import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import { SlackUser } from 'hooks/useSlackUsers'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      position: 'relative',
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      minHeight: 60,
      maxHeight: 200,
      overflowY: 'auto',
      backgroundColor: theme.palette.background.paper,
      cursor: 'text',
      width: '100%',
      flex: 1,
      '&:focus-within': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
        padding: theme.spacing(1.5) - 1, // Adjust for thicker border
      },
    },
    editable: {
      outline: 'none',
      minHeight: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      '&:empty::before': {
        content: 'attr(data-placeholder)',
        color: theme.palette.grey[500],
      },
    },
    mention: {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
  })
)

interface ContentEditableInputProps {
  value: string
  onChange: (value: string) => void
  onCursorPositionChange: (position: number) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  placeholder?: string
  taggedUsers: SlackUser[]
  cursorPosition?: number
}

export function ContentEditableInput({
  value,
  onChange,
  onCursorPositionChange,
  onKeyDown,
  placeholder = 'Type a message...',
  taggedUsers,
  cursorPosition,
}: ContentEditableInputProps) {
  const classes = useStyles()
  const editableRef = useRef<HTMLDivElement>(null)
  const isUpdatingRef = useRef(false)

  // Save and restore cursor position
  const saveCursorPosition = () => {
    const selection = window.getSelection()
    if (!editableRef.current || !selection || selection.rangeCount === 0) return 0

    const range = selection.getRangeAt(0)

    // Helper to count characters in a node tree
    const countCharsInNode = (node: Node): number => {
      let count = 0
      if (node.nodeType === Node.TEXT_NODE) {
        count = node.textContent?.length || 0
      } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR') {
        count = 1 // <br> counts as 1 newline character
      } else if (node.childNodes) {
        for (let i = 0; i < node.childNodes.length; i++) {
          count += countCharsInNode(node.childNodes[i])
        }
      }
      return count
    }

    // Walk through all nodes before the cursor and count characters
    let charCount = 0
    const walker = document.createTreeWalker(
      editableRef.current,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null
    )

    let currentNode = walker.nextNode()
    while (currentNode) {
      if (currentNode === range.endContainer) {
        // Found the cursor position
        if (currentNode.nodeType === Node.TEXT_NODE) {
          charCount += range.endOffset
        }
        break
      }

      // Check if the cursor is inside a child of this node
      if (currentNode.contains(range.endContainer)) {
        // Don't count this node, continue to its children
        currentNode = walker.nextNode()
        continue
      }

      // This node is completely before the cursor, count it
      if (currentNode.nodeType === Node.TEXT_NODE) {
        charCount += currentNode.textContent?.length || 0
      } else if (
        currentNode.nodeType === Node.ELEMENT_NODE &&
        (currentNode as Element).tagName === 'BR'
      ) {
        charCount += 1
      }

      currentNode = walker.nextNode()
    }

    return charCount
  }

  const restoreCursorPosition = (position: number) => {
    if (!editableRef.current) return

    const selection = window.getSelection()
    if (!selection) return

    let charCount = 0
    const nodeStack: Node[] = [editableRef.current]
    let node: Node | undefined
    let foundStart = false

    while (!foundStart && (node = nodeStack.pop())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text
        const nextCharCount = charCount + (textNode.textContent?.length || 0)
        if (position >= charCount && position <= nextCharCount) {
          const range = document.createRange()
          range.setStart(
            textNode,
            Math.min(position - charCount, textNode.textContent?.length || 0)
          )
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
          foundStart = true
        }
        charCount = nextCharCount
      } else if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'BR') {
        // Count <br> elements as newline characters (1 char each)
        charCount += 1
        if (position === charCount) {
          // Cursor is right after the <br>
          const range = document.createRange()
          range.setStartAfter(node)
          range.collapse(true)
          selection.removeAllRanges()
          selection.addRange(range)
          foundStart = true
        }
      } else {
        const childNodes = Array.from(node.childNodes)
        for (let i = childNodes.length - 1; i >= 0; i--) {
          nodeStack.push(childNodes[i])
        }
      }
    }
  }

  // Render text with mentions highlighted
  const renderContent = () => {
    if (!value) return ''

    let result: Array<{ text: string; isMention: boolean }> = [{ text: value, isMention: false }]

    // For each tagged user, split the text and mark mentions
    taggedUsers.forEach(user => {
      const mentionPattern = `@${user.displayName}`
      const newResult: Array<{ text: string; isMention: boolean }> = []

      result.forEach(segment => {
        if (segment.isMention) {
          newResult.push(segment)
        } else {
          const parts = segment.text.split(mentionPattern)
          parts.forEach((part, index) => {
            if (part) {
              newResult.push({ text: part, isMention: false })
            }
            if (index < parts.length - 1) {
              newResult.push({ text: mentionPattern, isMention: true })
            }
          })
        }
      })

      result = newResult
    })

    // Convert to HTML
    return result
      .map(segment => {
        const escapedText = segment.text.replace(/\n/g, '<br>')
        return segment.isMention
          ? `<span class="${classes.mention}">${escapedText}</span>`
          : escapedText
      })
      .join('')
  }

  // Only update HTML when explicitly needed (mention selection or clearing)
  useEffect(() => {
    if (!editableRef.current || isUpdatingRef.current) return

    // Only update if we have an explicit cursor position OR value is empty
    if (cursorPosition !== undefined) {
      // After selecting a mention, update HTML with highlighting
      isUpdatingRef.current = true
      const renderedContent = renderContent()
      editableRef.current.innerHTML = renderedContent
      restoreCursorPosition(cursorPosition)
      isUpdatingRef.current = false

      // Notify parent of the updated cursor position
      // This ensures the mention detection logic knows the cursor has moved
      onCursorPositionChange(cursorPosition)
    } else if (value === '') {
      // Clear the input
      isUpdatingRef.current = true
      editableRef.current.innerHTML = ''
      isUpdatingRef.current = false
    }
    // During normal typing (cursorPosition === undefined && value !== ''), don't touch innerHTML
  }, [cursorPosition, value, taggedUsers])

  // Handle input changes
  const handleInput = () => {
    if (!editableRef.current || isUpdatingRef.current) return

    const newValue = editableRef.current.innerText
    onChange(newValue)

    // Get and report cursor position
    const cursorPosition = saveCursorPosition()
    onCursorPositionChange(cursorPosition)
  }

  // Handle cursor position changes
  const handleSelect = () => {
    if (!editableRef.current || isUpdatingRef.current) return
    const cursorPosition = saveCursorPosition()
    onCursorPositionChange(cursorPosition)
  }

  // Focus the editable when container is clicked
  const handleContainerClick = () => {
    editableRef.current?.focus()
  }

  return (
    <div className={classes.container} onClick={handleContainerClick}>
      <div
        ref={editableRef}
        className={classes.editable}
        contentEditable
        onInput={handleInput}
        onKeyDown={onKeyDown}
        onKeyUp={handleSelect}
        onClick={handleSelect}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  )
}
